/**
 * Stock-In API Service V2
 * Unified API client with caching, WebSocket support, and fallback
 */

import axios from 'axios';
import io from 'socket.io-client';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

class StockInAPIService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.sessionId = null;
    this.listeners = new Map();
    
    // Feature flags
    this.useWebSocket = true;
    this.useParallelValidation = true;
    this.useJobQueue = true;
  }

  /**
   * Initialize WebSocket connection for real-time updates
   */
  initializeWebSocket(businessId) {
    if (!this.useWebSocket || this.isConnected) return;

    try {
      this.socket = io(API_BASE_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      this.socket.on('connect', () => {
        this.isConnected = true;
        
        // Register session
        this.sessionId = `stockin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        this.socket.emit('stockin:register', {
          sessionId: this.sessionId,
          businessId
        });
      });

      this.socket.on('disconnect', () => {
        this.isConnected = false;
      });

      this.socket.on('stockin:registered', (data) => {
      });

      // Setup event listeners for validation updates
      this.setupValidationListeners();

    } catch (error) {
      console.error('WebSocket initialization error:', error);
      this.useWebSocket = false;
    }
  }

  /**
   * Setup WebSocket listeners for validation events
   */
  setupValidationListeners() {
    if (!this.socket) return;

    this.socket.on('stockin:validation-progress', (data) => {
      this.notifyListeners('validation-progress', data);
    });

    this.socket.on('stockin:validation-complete', (data) => {
      this.notifyListeners('validation-complete', data);
    });

    this.socket.on('stockin:validation-error', (data) => {
      this.notifyListeners('validation-error', data);
    });

    this.socket.on('stockin:batch-item-complete', (data) => {
      this.notifyListeners('batch-item-complete', data);
    });

    this.socket.on('stockin:batch-validation-complete', (data) => {
      this.notifyListeners('batch-complete', data);
    });

    this.socket.on('stockin:semantic-results', (data) => {
      this.notifyListeners('semantic-results', data);
    });

    // OCR Real-time Event Listeners
    this.socket.on('ocr:started', (data) => {
      this.notifyListeners('ocr-started', data);
    });

    this.socket.on('ocr:stage-complete', (data) => {
      this.notifyListeners('ocr-stage-complete', data);
    });

    this.socket.on('ocr:vendor-extracted', (data) => {
      this.notifyListeners('ocr-vendor-extracted', data);
    });

    this.socket.on('ocr:item-extracted', (data) => {
      this.notifyListeners('ocr-item-extracted', data);
    });

    this.socket.on('ocr:complete', (data) => {
      this.notifyListeners('ocr-complete', data);
    });

    this.socket.on('ocr:error', (data) => {
      console.error('❌ OCR Error:', data.error);
      this.notifyListeners('ocr-error', data);
    });

    this.socket.on('ocr:aborted', (data) => {
      console.warn('🛑 OCR Aborted:', data.sessionId);
      this.notifyListeners('ocr-aborted', data);
    });

    this.socket.on('ocr:abort-confirmed', (data) => {
      this.notifyListeners('ocr-abort-confirmed', data);
    });
  }

  /**
   * Subscribe to validation events
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    
    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from validation events
   */
  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Notify all listeners for an event
   */
  notifyListeners(event, data) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }

  /**
   * Get axios instance with auth headers
   */
  getAxiosInstance() {
    const token = localStorage.getItem('token');
    const businessId = localStorage.getItem('businessId');

    return axios.create({
      baseURL: `${API_BASE_URL}/stock-in/v2`,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(businessId && { 'X-Business-Id': businessId })
      }
    });
  }

  /**
   * Validate single item (with WebSocket streaming if available)
   */
  async validateItem(item) {
    // Try WebSocket first for real-time streaming
    if (this.useWebSocket && this.isConnected && this.socket) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Validation timeout'));
        }, 30000);

        // Listen for completion
        const completeHandler = (data) => {
          if (data.item === item.item_name) {
            clearTimeout(timeout);
            this.off('validation-complete', completeHandler);
            this.off('validation-error', errorHandler);
            resolve(data.result);
          }
        };

        const errorHandler = (data) => {
          if (data.item === item.item_name) {
            clearTimeout(timeout);
            this.off('validation-complete', completeHandler);
            this.off('validation-error', errorHandler);
            reject(new Error(data.error));
          }
        };

        this.on('validation-complete', completeHandler);
        this.on('validation-error', errorHandler);

        // Emit validation request
        this.socket.emit('stockin:validate-item', {
          sessionId: this.sessionId,
          item
        });
      });
    }

    // Fallback to HTTP API
    const api = this.getAxiosInstance();
    const response = await api.post('/validate-item', item);
    return response.data.data;
  }

  /**
   * Validate batch of items (parallel processing)
   */
  async validateBatch(items) {
    // Try WebSocket batch validation for real-time progress
    if (this.useWebSocket && this.isConnected && this.socket && this.useParallelValidation) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Batch validation timeout'));
        }, 120000); // 2 minutes

        const completeHandler = (data) => {
          clearTimeout(timeout);
          this.off('batch-complete', completeHandler);
          resolve(data.results);
        };

        this.on('batch-complete', completeHandler);

        // Emit batch validation request
        this.socket.emit('stockin:validate-batch', {
          sessionId: this.sessionId,
          items
        });
      });
    }

    // Fallback to HTTP batch API
    const api = this.getAxiosInstance();
    const response = await api.post('/validate-batch', { items });
    return response.data.data;
  }

  /**
   * Upload receipt for OCR processing
   */
  async uploadReceipt(file, onProgress = null, opts = {}) {
    const api = this.getAxiosInstance();
    const formData = new FormData();
    if (file) formData.append('receipt', file);
    // Bill Memory: a bill pasted as text, sent instead of a file (the server reads it only while Bill Memory is not Off).
    if (opts && opts.billText) formData.append('bill_text', String(opts.billText));
    // Bill Memory: a vendor the user picked (certain) — read only by Bill Memory, never by today's vendor memory (design P3).
    if (opts && opts.bmVendorId) formData.append('bm_vendor_id', String(opts.bmVendorId));

    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(onProgress && {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      })
    };

    const response = await api.post('/upload-receipt', formData, config);
    
    // Transform V2 response format to match old OCR format (for StockInForm compatibility)
    const transformedData = this.transformV2ToLegacyFormat(response.data);
    
    // If using job queue, poll for result
    if (response.data.jobId && this.useJobQueue) {
      const result = await this.pollJobResult(response.data.queue, response.data.jobId);
      return this.transformV2ToLegacyFormat(result);
    }

    return transformedData;
  }

  /**
   * Abort active OCR processing
   * @param {string} sessionId - OCR session ID to abort
   * @returns {Promise<Object>} Abort confirmation
   */
  async abortOCR(sessionId) {
    try {
      const api = this.getAxiosInstance();
      const response = await api.post(`/abort-ocr/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Abort OCR error:', error);
      throw new Error(error.response?.data?.error || 'Failed to abort OCR');
    }
  }

  /**
   * Transform V2 OCR response to legacy format for StockInForm compatibility
   * V2 uses: { name, quantity, unit_price, ... }
   * Legacy expects: { item_name, quantity, unit_price, ... }
   */
  transformV2ToLegacyFormat(data) {
    if (!data || !data.items) {
      return data;
    }

    return {
      ...data,
      items: data.items.map(item => ({
        ...item,
        item_name: item.name || item.item_name, // V2 uses 'name', legacy uses 'item_name'
        // Keep all other V2 fields as-is (quantity, unit_price, total_price, etc.)
      }))
    };
  }

  /**
   * Poll job result until completion
   */
  async pollJobResult(queue, jobId, maxAttempts = 60) {
    const api = this.getAxiosInstance();
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await api.get(`/job-result/${queue}/${jobId}`);
      
      if (response.data.success && response.data.state === 'completed') {
        return response.data.result;
      } else if (response.data.state === 'failed') {
        throw new Error(response.data.error || 'Job failed');
      }
      
      // Wait before next poll (exponential backoff)
      const delay = Math.min(1000 * Math.pow(1.5, attempt), 5000);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    throw new Error('Job timeout - result not available');
  }

  /**
   * Submit stock-in record (unified endpoint)
   */
  async submitStockIn(vendor, items, metadata) {
    const api = this.getAxiosInstance();
    
    const response = await api.post('/submit', {
      vendor,
      items,
      metadata
    });

    return response.data;
  }

  /**
   * Semantic search for item autocomplete
   */
  async semanticSearch(query) {
    // Try WebSocket first for faster response
    if (this.useWebSocket && this.isConnected && this.socket) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Search timeout'));
        }, 5000);

        const resultHandler = (data) => {
          if (data.query === query) {
            clearTimeout(timeout);
            this.off('semantic-results', resultHandler);
            resolve(data.matches);
          }
        };

        this.on('semantic-results', resultHandler);

        this.socket.emit('stockin:semantic-search', {
          sessionId: this.sessionId,
          query
        });
      });
    }

    // Fallback to HTTP API
    const api = this.getAxiosInstance();
    const response = await api.get('/semantic-search', { params: { query } });
    return response.data.data;
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    const api = this.getAxiosInstance();
    const response = await api.get('/cache-stats');
    return response.data.data;
  }

  /**
   * Get queue statistics
   */
  async getQueueStats() {
    const api = this.getAxiosInstance();
    const response = await api.get('/queue-stats');
    return response.data.data;
  }

  /**
   * Get circuit breaker status
   */
  async getCircuitBreakerStatus() {
    const api = this.getAxiosInstance();
    const response = await api.get('/circuit-breaker-status');
    return response.data.data;
  }

  /**
   * Clear cache for current business
   */
  async clearCache() {
    const api = this.getAxiosInstance();
    const response = await api.delete('/cache');
    return response.data;
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.socket && this.isConnected) {
      this.socket.emit('stockin:unregister', { sessionId: this.sessionId });
      this.socket.disconnect();
      this.isConnected = false;
      this.sessionId = null;
    }
  }

  /**
   * Enable/disable features
   */
  setFeatureFlags(flags) {
    if (flags.useWebSocket !== undefined) this.useWebSocket = flags.useWebSocket;
    if (flags.useParallelValidation !== undefined) this.useParallelValidation = flags.useParallelValidation;
    if (flags.useJobQueue !== undefined) this.useJobQueue = flags.useJobQueue;
  }
}

// Export singleton instance
const stockInAPI = new StockInAPIService();
export default stockInAPI;
